import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Resultdisplay from '../components/Resultsdisplay';
import Search from '../components/Search';
import MapDebug from '../components/utils/MapDebug';
import logo from '../assets/images/waativalogoteksilla.png';
import { Building2, FileText } from 'lucide-react';

const BRAND = '#04aa00';
const BRAND_DARK = '#027000';

// Golden ratio spacing scale (φ = 1.618)
// GR1=1rem  GR2=1.618rem  GR3=2.618rem  GR4=4.236rem
const GR2 = '1.618rem';
const GR3 = '2.618rem';
const GR4 = '4.236rem';

function Home() {
  const [kiinteistoObjects, setKiinteistoOjbects] = useState([]);
  const [kiinteistotJson, setKiinteistotJson] = useState([]);
  const [heroVisible, setHeroVisible] = useState(false);
  const resultsRef = useRef(null);

  const afterSearch = (results) => setKiinteistoOjbects(results);

  useEffect(() => {
    if (kiinteistoObjects.length > 0) setKiinteistotJson(kiinteistoObjects);
  }, [kiinteistoObjects]);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const hasResults = kiinteistotJson.length > 0;

  return (
    <div className="main-content" style={{ minHeight: '100vh', background: '#f5f7f5' }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section
        className="home-hero"
        style={{
          background: `linear-gradient(145deg, ${BRAND_DARK} 0%, ${BRAND} 60%, #39c435 100%)`,
          position: 'relative',
          overflow: 'hidden',
          paddingTop: GR4,
          paddingBottom: GR4,
          transition: 'opacity 0.6s ease, transform 0.6s ease',
          opacity: heroVisible ? 1 : 0,
          transform: heroVisible ? 'translateY(0)' : 'translateY(-12px)',
        }}
      >
        {/* Dot-grid texture */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
        {/* Blob accents */}
        <div style={{
          position: 'absolute', right: '-80px', top: '-80px',
          width: 340, height: 340, borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)', filter: 'blur(60px)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', left: '-60px', bottom: '-40px',
          width: 260, height: 260, borderRadius: '50%',
          background: 'rgba(0,0,0,0.06)', filter: 'blur(50px)', pointerEvents: 'none',
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>

          {/* Logo */}
          <div className="text-center home-logo-wrap" style={{ marginBottom: GR3 }}>
            <img
              src={logo}
              alt="Waativa"
              className="home-logo"
              style={{ height: 48, filter: 'brightness(0) invert(1)', opacity: 0.92 }}
            />
          </div>

          {/* Search bar — same width as the cards/results slot below */}
          <div style={{ maxWidth: 776, margin: `0 auto ${GR2}` }}>
            <div style={{
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(10px)',
              borderRadius: 14,
              padding: '0.75rem 1rem',
              border: '1px solid rgba(255,255,255,0.22)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Search afterSearch={afterSearch} />
            </div>
          </div>

          {/* Results / Quick-access — wider than search bar (480 × φ ≈ 776) */}
          <div style={{ maxWidth: 776, margin: '0 auto' }}>
            {hasResults ? (
              <div
                ref={resultsRef}
                className="home-results"
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  padding: GR3,
                }}
              >
                <Resultdisplay data={kiinteistotJson} setMapCoords={() => {}} />
              </div>
            ) : (
              <div className="row g-3">
                <QuickCard
                  to="/taloyhtiokortit"
                  icon={<Building2 size={20} />}
                  title="Taloyhtiokortit"
                  desc="Selaa ja hallinnoi luotuja taloyhtiökortteja."
                />
                <QuickCard
                  to="/raportit"
                  icon={<FileText size={20} />}
                  title="Raportit"
                  desc="Selaa valmiita ja keskeneräisiä raportteja."
                />
              </div>
            )}
          </div>

        </div>
      </section>

      <MapDebug />

      <style>{`
        /* ── Mobile overrides ─────────────────────────────── */
        @media (max-width: 576px) {
          .home-hero {
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
          }
          .home-logo-wrap {
            margin-bottom: 1.5rem !important;
          }
          .home-logo {
            height: 34px !important;
          }
          .home-results {
            padding: 1rem !important;
            border-radius: 12px !important;
          }
          .quick-card-inner {
            padding: 0.75rem !important;
            gap: 0.65rem !important;
          }
          .quick-card-icon {
            width: 36px !important;
            height: 36px !important;
            border-radius: 8px !important;
          }
          .quick-card-desc {
            display: none !important;
          }
          .quick-card-title {
            font-size: 0.85rem !important;
          }
        }

        /* ── Tablet tweaks ────────────────────────────────── */
        @media (min-width: 577px) and (max-width: 768px) {
          .home-hero {
            padding-top: 3rem !important;
            padding-bottom: 3rem !important;
          }
          .home-results {
            padding: 1.618rem !important;
          }
        }
      `}</style>
    </div>
  );
}

function QuickCard({ to, icon, title, desc }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div className="col-6">
      <Link
        to={to}
        className="text-decoration-none d-block h-100"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          className="quick-card-inner"
          style={{
            background: hovered ? '#fff' : 'rgba(255,255,255,0.13)',
            border: `1px solid ${hovered ? '#fff' : 'rgba(255,255,255,0.2)'}`,
            borderRadius: 12,
            padding: '1rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            height: '100%',
            transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.25s ease',
            transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
            boxShadow: hovered
              ? `0 8px 24px rgba(0,0,0,0.2), 0 0 32px rgba(255,255,255,0.25)`
              : '0 2px 8px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(8px)',
            cursor: 'pointer',
          }}
        >
          <div
            className="quick-card-icon"
            style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              background: hovered ? BRAND : 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
              transition: 'background 0.2s ease',
            }}
          >
            {icon}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              className="quick-card-title"
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontWeight: 700,
                fontSize: '0.95rem',
                color: hovered ? BRAND_DARK : '#fff',
                letterSpacing: '-0.1px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </div>
            <div
              className="quick-card-desc"
              style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '0.82rem',
                color: hovered ? '#5a7a5a' : 'rgba(255,255,255,0.78)',
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {desc}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default Home;
